import _objectSpread from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import _objectWithoutProperties from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "content", "onOpenChange", "open", "defaultOpen", "forceMount", "__checkInteractivity"],
    _excluded2 = ["children", "variant", "className"];
import { jsxs, jsx } from 'react/jsx-runtime';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import clsx from 'clsx';
import React__default, { useContext } from 'react';
import { useForkRef } from '../utils/useForkRef.js';
import { useIsTriggerInteractive } from '../utils/useIsTriggerInteractive.js';
import styles from './tooltip.module.js';
/**
 * A component used to wrap any other component, giving it a Tooltip on hover.
 *
 * * [Radix-UI link](https://www.radix-ui.com/docs/primitives/components/tooltip)
 *
 * **IMPORTANT (1)**: Must be placed inside a single <b>TooltipProvider</b> component provided by the app, see [issue on github](https://github.com/radix-ui/primitives/issues/1712) and [radix docs](https://www.radix-ui.com/docs/primitives/components/tooltip#provider).
 *
 * **IMPORTANT (2)**: Must wrap interactive component/element (such as: HTMLInputElement, HTMLAnchorElement,  HTMLTextAreaElement, etc. [list of focusable elements](https://allyjs.io/data-tables/focusable.html))
 *
 */

var Tooltip = function Tooltip(props) {
  var children = props.children,
      content = props.content,
      onOpenChange = props.onOpenChange,
      open = props.open,
      defaultOpen = props.defaultOpen,
      forceMount = props.forceMount,
      __checkInteractivity = props.__checkInteractivity,
      contentProps = _objectWithoutProperties(props, _excluded);

  var container = useContext(ContainerContext);
  return jsxs(RadixTooltip.Root, {
    defaultOpen: defaultOpen,
    onOpenChange: onOpenChange,
    open: open,
    children: [jsx(TooltipTrigger, {
      __checkInteractivity: __checkInteractivity,
      open: open,
      children: children
    }), jsx(RadixTooltip.Portal, {
      container: container,
      forceMount: forceMount,
      children: jsx(Content, _objectSpread(_objectSpread({}, contentProps), {}, {
        children: content
      }))
    })]
  });
};

var ContainerContext = /*#__PURE__*/React__default.createContext(null);

var TooltipProvider = function TooltipProvider(props) {
  var children = props.children,
      container = props.container,
      disableHoverableContent = props.disableHoverableContent;
  return jsx(ContainerContext.Provider, {
    value: container,
    children: jsx(RadixTooltip.Provider, {
      delayDuration: 300,
      disableHoverableContent: disableHoverableContent,
      children: children
    })
  });
};

var Trigger = /*#__PURE__*/React__default.forwardRef(function Trigger(props, ref) {
  return jsx(RadixTooltip.Trigger, {
    ref: ref,
    asChild: true,
    children: props.children
  });
});
/**
 *  Trigger component with interactivity check.
 *  Encapsulation in separate component is a workaround which allows to render hooks
 *  responsible for check only when it's specifically needed, without breaking rule of hooks
 *  by calling them conditionally.
 */

var TriggerWithInteractivityCheck = /*#__PURE__*/React__default.forwardRef(function TriggerWithInteractivityCheck(props, ref) {
  var triggerRef = useIsTriggerInteractive();
  return jsx(Trigger, {
    ref: useForkRef(triggerRef, ref),
    children: props.children
  });
});
var TooltipTrigger = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      open = _ref.open,
      _ref$__checkInteracti = _ref.__checkInteractivity,
      __checkInteractivity = _ref$__checkInteracti === void 0 ? true : _ref$__checkInteracti;

  // in dev environment, if Tooltip open state is not explicitly controlled by user, check if it's used with interactive element
  if (process.env.NODE_ENV !== 'production' && open === undefined && __checkInteractivity) {
    return jsx(TriggerWithInteractivityCheck, {
      ref: ref,
      children: children
    });
  }

  return jsx(Trigger, {
    ref: ref,
    children: children
  });
});
var Arrow = /*#__PURE__*/React__default.forwardRef(function Arrow(_ref2, ref) {
  var variant = _ref2.variant;
  return jsx("div", {
    ref: ref,
    className: clsx(styles.arrow, styles[variant])
  });
});
var Content = /*#__PURE__*/React__default.forwardRef(function Content(props, ref) {
  var children = props.children,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'standard' : _props$variant,
      className = props.className,
      rest = _objectWithoutProperties(props, _excluded2);

  return jsxs(RadixTooltip.Content, _objectSpread(_objectSpread({}, rest), {}, {
    ref: ref,
    className: clsx(styles.content, styles[variant], className),
    sideOffset: 3,
    children: [children, jsx(RadixTooltip.Arrow, {
      asChild: true,
      children: jsx(Arrow, {
        variant: variant
      })
    })]
  }));
});
export { ContainerContext, Tooltip, TooltipProvider, TooltipTrigger };